/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Options } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';

import Retracked from 'js/lib/retracked';

import { Typography, typography2 } from '@coursera/cds-core';

import EnterpriseArticleRichText, {
  getDefaultRichTextOptions,
} from 'bundles/ent-website/articles/components/EnterpriseArticleRichText';
import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';
import { getDefaultArticleSourceRenderNodes } from 'bundles/shared-articles/components/RichTextNodeRenderers';

import _t from 'i18n!nls/ent-website';

type Props = {
  body: Document;
  module: BlockLevelModule;
};

const styles = {
  sessionTitle: () => css`
    font-size: 24px;
    margin-bottom: var(--cds-spacing-150);
  `,
  sources: () => css`
    div[role='list']:not(.cds-ShowMoreList-expanded) {
      background-color: var(--cds-color-neutral-primary);
      background-image: linear-gradient(
        to bottom,
        var(--cds-color-neutral-primary),
        var(--cds-color-neutral-background-primary)
      );
      -webkit-background-clip: text;
      -moz-background-clip: text;
      -webkit-text-fill-color: transparent;
      -moz-text-fill-color: transparent;
    }

    div[role='listitem'] {
      ${typography2.bodyPrimary}
      font-size: 18px;
      line-height: 32px;
      margin-bottom: 0;
      display: flex;

      p {
        ${typography2.bodyPrimary}
        font-size: 18px;
        line-height: 32px;
        margin-bottom: 0;
        display: inline-block;

        a {
          color: var(--cds-color-blue-700);
          text-decoration: underline;
        }
      }
    }

    div > .cds-ShowMoreList-ctaButton {
      ${typography2.subtitleMedium}
      color: var(--cds-color-blue-700);
      margin-top: 0;
    }
  `,
};

const logArticlesSourcesEvent = (isExpanded: boolean) => {
  const CONTEXT_DATA = {
    namespace: {
      app: 'ent-website',
      page: 'article_page',
    },
  };
  const trackingName = isExpanded ? 'article_show_more_sources' : 'article_show_less_sources';
  Retracked.trackComponent(CONTEXT_DATA, { page: window.location.pathname }, trackingName, 'click');
};

const getArticleSourceRichTextOptions = (module: BlockLevelModule) => (): Options => {
  return {
    renderNode: {
      ...getDefaultRichTextOptions(module).renderNode,
      ...getDefaultArticleSourceRenderNodes(logArticlesSourcesEvent),
    },
  };
};

const EnterpriseArticleSources = ({ body, module }: Props) => {
  if (!body) return null;

  return (
    <React.Fragment>
      <Typography css={styles.sessionTitle} variant="h1semibold" component="h2">
        {_t('Article sources')}
      </Typography>
      <EnterpriseArticleRichText
        css={styles.sources}
        content={body}
        getRichTextOptions={getArticleSourceRichTextOptions(module)}
        module={module}
      />
    </React.Fragment>
  );
};

export default EnterpriseArticleSources;
